import React, { ReactNode, useCallback, useEffect } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { ActionLink } from 'components/action-button';
import Modal, { ModalContent } from 'components/modal';
import { useCart } from 'hooks/use-cart';
import OrderIssues from 'pages/order-confirmation/order-issues';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import HeadingContainer from './cart-error-heading-wrapper';

const ButtonGutter = Box.withConfig({
  flexDirection: { base: 'column', md: 'row' },
  justifyContent: { base: 'center', md: 'space-between' },
  marginTop: '$8',
});

const Body = Text.withConfig({
  marginX: 0,
  marginTop: 2,
  textAlign: 'justify',
});

const Disclaimer = Text.withConfig({
  variant: 'copyTwo',
  maxWidth: '$64',
  textAlign: 'center',
  marginTop: '$8',
});

const LinkWrapper = Box.withConfig({
  textAlign: 'center',
});

interface ICartError {
  title?: string;
  body?: ReactNode;
  disclaimer?: string;
  startOver?: boolean;
  onAccept?: () => void;
  onDismiss?: () => void;
  additionalLoggingContext: Object;
}

const message = 'Cart Error: Order could not be completed';

const CartError: React.FC<React.PropsWithChildren<ICartError>> = ({
  title,
  body,
  disclaimer,
  onAccept,
  startOver,
  onDismiss,
  additionalLoggingContext,
}) => {
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);
  const { resetStore } = useStoreContext();
  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { formatMessage } = useIntl();

  // log once on mount to keep better track of these backend errors preventing checkout
  useEffect(() => {
    logger.error({
      message,
      isErrorModal: true,
      context: {
        ...additionalLoggingContext,
        guestMessageTitle: title,
        isOrderCouldNotBeCompletedError: true,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { emptyCart } = useCart();

  const startOverCallback = useCallback(() => {
    shouldConfirmStoreOnCheckout(true);
    emptyCart();
    resetStore();
  }, [emptyCart, resetStore, shouldConfirmStoreOnCheckout]);

  const modalHeading = title || formatMessage({ id: 'restaurantUnavailableErrorTitle' });
  const modalBody = body || formatMessage({ id: 'orderNotCompleted' });

  return (
    <Modal
      onDismiss={onDismiss}
      isErrorModal
      amplitudeEventData={{
        modalAppearanceEventMessage: message,
        modalMessage: typeof modalBody === 'string' ? modalBody : undefined,
      }}
      backgroundColor={theme.token('background-default')}
      footer={isShowingOrderingIssue && <OrderIssues />}
    >
      <ModalContent alignItems="center" paddingX="$8" testID="cart-error-modal-content">
        <HeadingContainer modalHeading={modalHeading} />
        <Body>{modalBody}</Body>
        <Disclaimer>
          {disclaimer || formatMessage({ id: 'restartPurchaseProcessDisclaimer' })}
        </Disclaimer>
        <ButtonGutter>
          {onAccept && (
            <LinkWrapper>
              <ActionLink onPress={onAccept} to={'.'} fullWidth>
                {formatMessage({ id: 'accept' })}
              </ActionLink>
            </LinkWrapper>
          )}
          {startOver && (
            <LinkWrapper>
              <ActionLink
                to={routes.menu}
                onPress={startOverCallback}
                testID="cart-error-start-over"
                fullWidth
              >
                {formatMessage({ id: 'restartPurchaseProcessButton' })}
              </ActionLink>
            </LinkWrapper>
          )}
        </ButtonGutter>
      </ModalContent>
    </Modal>
  );
};

export default CartError;
